/* eslint-disable max-len */
/* eslint-disable no-unused-vars */
import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import moment from 'moment';
import { PlusIcon } from 'lucide-react';
import { BaseModal } from '@/Components/Modals/BaseModal';
import { modalActions } from '@/Actions';
import styles from './createShipmentModal.module.scss';
import { Button } from '@/Components/ui/button';
import {
	addCommas, baseUnits, getUnitConvertedQuantity,
	inputHelper
} from '@/Helpers';
import inventoryAnalyticsServices from '@/Services/Analytics Services/inventory-analytics.services';
import { notificationServices } from '@/Services';

function CreateShipmentModal(props) {
	const { fetchPo = () => { }, shipmentMode = 'po' } = props;
	const { show = false, data = {} } = useSelector((state) => state?.modal?.shipmentCreateModal);
	const [orderedItems, setOrderedItems] = useState([]);
	const [showOtherCharges, setShowOtherCharges] = useState(false);
	const [charges, setCharges] = useState({});
	useEffect(() => {
		const items = data?.items?.map((item) => {
			let qty = 0;
			if (item?.qty > item?.fulfilled) {
				qty = getQuantity(item, item?.qty) - getQuantity(item, item?.fulfilled);
			} else {
				qty = 0;
			}
			return {
				...item,
				shipmentQty: ['count', 'pcs'].includes(item?.unit) ? qty : qty.toFixed(2),
			};
		});
		setOrderedItems(items);
		setCharges({
			deliveryCharges: {
				deliveryCharges: data?.deliveryCharges,
				taxPer: data?.deliveryTaxPercentage,
				taxValue: (Number(data?.deliveryTaxPercentage) / 100) * data?.deliveryCharges,
			},
			packagingCharges: {
				packagingCharges: data?.packagingCharges,
				taxPer: data?.packagingTaxPercentage,
				taxValue: (Number(data?.packagingTaxPercentage) / 100) * data?.packagingCharges,
			},
			otherCharges: {
				otherCharges: '',
				taxPer: '',
			},
		});
	}, [data]);
	const dispatch = useDispatch();
	const handleClose = () => {
		setCharges({});
		setShowOtherCharges(false);
		setOrderedItems([]);
		dispatch(modalActions.toggleCreateShipmentModal(false));
	};

	const handleSave = async () => {
		try {
			const response = {
				data: {
					poId: data?.id,
					deliveryCharges: parseFloat(charges?.deliveryCharges?.deliveryCharges),
					deliveryTaxPercentage: parseFloat(charges?.deliveryCharges?.taxPer),
					packagingCharges: parseFloat(charges?.packagingCharges?.packagingCharges),
					packagingTaxPercentage: parseFloat(charges?.packagingCharges?.taxPer),
					extraData: {
						additionalCharges: [{
							tax: (Number(charges?.otherCharges?.taxPer) / 100) * parseFloat(charges?.otherCharges?.otherCharges),
							type: 'Additional Charges',
							price: parseFloat(charges?.otherCharges?.otherCharges),
							taxPercentage: parseFloat(charges?.otherCharges?.taxPer),
						}],
					},
					items: orderedItems.map((item) => ({
						...item,
						shipmentQty: parseFloat(getUnitConvertedQuantity(item?.shipmentQty, item?.unit, true, true, true)),
					})),
				},
			};
			if (data?.handleSave) {
				data?.handleSave(response);
				return;
			}
			const res = await inventoryAnalyticsServices.createShipment(response);
			if (res?.statusCode === 200) {
				fetchPo();
				handleClose();
			}
		} catch (error) {
			notificationServices?.generateNotification({
				message: error,
				type: 'error',
			});
		}
	};

	const renderBtns = () => {
		return (
			<div className='w-full flex justify-center items-start gap-2'>
				<Button variant='default' className='w-1/2 px-4 py-2 bg-slate-200 text-black' onClick={handleClose}>Cancel</Button>
				<Button variant='default' className='w-1/2 px-4 py-2 bg-yellow-500 text-black' onClick={handleSave}>Create shipment</Button>
			</div>
		);
	};

	function getQuantity(item, val = '') {
		if (baseUnits?.includes(item?.unit)) {
			return getUnitConvertedQuantity(
				val,
				item?.unit,
				true,
				true
			);
		}
		return val;
	}

	const handleShipmentQty = (id, value) => {
		const val = value < 0 ? '' : value;
		const items = orderedItems?.map((item) => (Number(item?.id) === Number(id)
			? {
				...item, shipmentQty: parseFloat(val),
			} : item));
		setOrderedItems(items);
	};

	const handleCharges = (e) => {
		const { fieldName, fieldValue } = inputHelper(e);
		const value = fieldValue < 0 ? '' : fieldValue;
		if (fieldName === 'deliveryCharges') {
			return setCharges({
				...charges,
				deliveryCharges: {
					...charges.deliveryCharges,
					deliveryCharges: value,
					taxValue: (Number(charges?.deliveryCharges?.taxPer) / 100) * value,
				},
			});
		}
		if (fieldName === 'packagingCharges') {
			return setCharges({
				...charges,
				packagingCharges: {
					...charges.packagingCharges,
					packagingCharges: value,
					taxValue: (Number(charges?.packagingCharges?.taxPer) / 100) * value,
				},
			});
		}
		if (fieldName === 'otherCharges') {
			return setCharges({
				...charges,
				otherCharges: {
					...charges.otherCharges,
					otherCharges: value,
				},
			});
		}
		if (fieldName === 'otherTaxper') {
			return setCharges({
				...charges,
				otherCharges: {
					...charges.otherCharges,
					taxPer: value,
				},
			});
		}
		return true;
	};
	const handleParentDocKeyDown = (e) => {
		if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
			e.stopPropagation();
			e.preventDefault(); // only if you also don’t want default scrolling
		}
	};

	return (
		<BaseModal
			open={show}
			width='40rem'
			height='45rem'
			handleClose={handleClose}
			renderBtns={renderBtns}
			showClose={false}
			onKeyDownCapture={handleParentDocKeyDown}
		>
			<div className='p-6'>
				<div className='mb-6'>
					<p className='text-start font-semibold text-lg mb-2 py-1'>Create new Shipment</p>
					<div className='flex flex-wrap items-start  gap-4 md:gap-6'>
						<div className='flex items-center gap-3 md:gap-4'>
							<div className='text-left'>
								<label className='block text-xs text-gray-500 mb-1'>Deliver From</label>
								<div className='text-sm font-medium text-gray-800'>{data?.fromStoreName}</div>
							</div>
							<div className='flex items-center justify-center w-8 h-8 bg-gray-50 border border-gray-300 rounded-full text-gray-600 text-lg font-bold'>
								→
							</div>

							<div className='text-left'>
								<label className='block text-xs text-gray-500 mb-1'>Deliver To</label>
								<div className='text-sm font-medium text-gray-800'>{shipmentMode === 'po' ? data?.storeName : data?.vendorName}</div>
							</div>
						</div>
						<div className='hidden md:block w-px h-12 bg-gray-200' />
						<div className='flex items-start gap-6'>
							<div className='text-left'>
								<label className='block text-xs text-gray-500 mb-1'>PO ID</label>
								<div className='text-sm text-gray-800'>{data?.id}</div>
							</div>
							<div className='text-left'>
								<label className='block text-xs text-gray-500 mb-1'>Order Date</label>
								<div className='text-sm text-gray-800'>{moment(data?.createdAt).format('Do MMMM YY')}</div>
							</div>
						</div>
					</div>

					<div className='border-t border-black-200'>
						<div className='mt-4 flex gap-2'>
							<div className='text-start w-[48%]'>
								<label className='block text-xs text-gray-500 mb-1'>Delivery Charges</label>
								<div className='text-sm text-gray-800 flex flex-col justify-start'>
									<input
										type='number'
										name='deliveryCharges'
										value={charges?.deliveryCharges?.deliveryCharges}
										className='w-full px-2 py-1 border border-gray-300 rounded text-sm text-center focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500'
										onChange={(e) => handleCharges(e)}
									/>
									<span className='text-[9.5px] text-[#271d0e] font-bold'>
										Tax (
										{charges?.deliveryCharges?.taxPer}
										%): ₹
										{charges?.deliveryCharges?.taxValue?.toFixed(2)}
									</span>
								</div>
							</div>
							<div className='text-start w-[48%]'>
								<label className='block text-xs text-gray-500 mb-1'>Packaging Charges</label>
								<div className='text-sm text-gray-800 flex flex-col'>
									<input
										type='number'
										name='packagingCharges'
										value={charges?.packagingCharges?.packagingCharges}
										className='w-full px-2 py-1 border border-gray-300 rounded text-sm text-center focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500'
										onChange={(e) => handleCharges(e)}
									/>
									<span className='text-[9.5px] text-[#271d0e] font-bold'>
										Tax(
										{charges?.packagingCharges?.taxPer}
										%): ₹
										{charges?.packagingCharges?.taxValue?.toFixed(2)}
									</span>
								</div>
							</div>
						</div>
						{showOtherCharges ? (
							<div className='text-start mt-2'>
								<label className='block text-xs text-gray-500 mb-1'>Other Charges</label>
								<div className='flex items-center gap-2'>
									<input
										type='number'
										name='otherCharges'
										placeholder='Amount (₹)'
										value={charges?.otherCharges?.otherCharges}
										className='w-[48%] px-2 py-1 border border-gray-300 rounded text-sm text-center focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500'
										onChange={(e) => handleCharges(e)}
									/>
									<input
										type='number'
										name='otherTaxper'
										placeholder='Tax (%)'
										value={charges?.otherCharges?.taxPer}
										className='w-[48%] px-2 py-1 border border-gray-300 rounded text-sm text-center focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500'
										onChange={(e) => handleCharges(e)}
									/>
								</div>
							</div>
						) : (
							<div
								className='text-start mt-2 text-xs font-semibold text-gray-500 mb-1 cursor-pointer flex items-center'
								onClick={() => setShowOtherCharges(true)}
							>
								<PlusIcon size={14} />
								Add Other Charges
							</div>
						)}
					</div>
				</div>

				<div className={`max-h-[300px] overflow-y-auto ${styles.customScroll}`}>
					<table className='w-full border-collapse'>
						<thead className='bg-yellow-200 sticky top-0 z-10'>
							<tr>
								<th className='px-4 py-3 text-left font-semibold text-sm bg-yellow-200'>Variant Name</th>
								<th className='px-4 py-3 text-center font-semibold text-sm bg-yellow-200'>Unit</th>
								<th className='px-4 py-3 text-center font-semibold text-sm bg-yellow-200'>PO Qty</th>
								<th className='px-4 py-3 text-center font-semibold text-sm bg-yellow-200'>Shipment Qty</th>
								{/* <th className='px-4 py-3 text-center font-semibold text-sm bg-yellow-200'>Total Amount</th> */}
							</tr>
						</thead>
						<tbody className='bg-white'>
							{(orderedItems || [])?.map((item) => {
								const variantName = `${item?.productName} ${item?.variantName
									? ` (${item?.variantName})`
									: ''}`;
								const qty = getQuantity(item, item?.qty);
								return (
									<tr className='border-b border-gray-200'>
										<td className='px-4 py-3 text-sm text-gray-800 '>{variantName}</td>
										<td className='px-4 py-3 text-sm text-gray-800 text-center'>{item?.unit}</td>
										<td className='px-4 py-3 text-sm text-gray-800 text-center'>{qty}</td>
										<td className='px-4 py-3 text-center '>
											<input
												type='number'
												value={item?.shipmentQty}
												className='w-20 px-2 py-1 border border-gray-300 rounded text-sm text-center focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500'
												onChange={(e) => handleShipmentQty(item?.id, e.target.value)}
											/>
										</td>
										{/* <td className='px-4 py-3 text-sm text-gray-800 text-center'>
											₹
											{addCommas(item?.totalValue ?? item?.finalAmount) ?? '-'}
										</td> */}
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</div>
		</BaseModal>
	);
}

export default CreateShipmentModal;
